import { computed, ref } from 'vue'
import initTallyForm, { TallyFormId } from '@/lib/tally'

const STORAGE_KEY_LAST_OPEN_MONTH = 'scheduled_form_last_open_month'
const STORAGE_KEY_TOP_STAKER_SHOWN_BY_WALLET = 'top_staker_form_shown_by_wallet'
const DAYS_WINDOW = 5 // Last N days of each month

function getCurrentMonthKey(): string {
  const now = new Date()
  const year = now.getUTCFullYear()
  const month = String(now.getUTCMonth() + 1).padStart(2, '0')
  return `${year}-${month}`
}

function isWithinLastNDays(n: number): boolean {
  const now = new Date()
  // Local calendar days for the user
  const year = now.getFullYear()
  const monthIndex = now.getMonth() // 0-11
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate()
  return now.getDate() > (daysInMonth - n)
}

function getLastOpenMonth(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY_LAST_OPEN_MONTH)
  } catch {
    return null
  }
}

function setLastOpenMonth(monthKey: string): void {
  try {
    localStorage.setItem(STORAGE_KEY_LAST_OPEN_MONTH, monthKey)
  } catch {
    // ignore
  }
}

function getTopStakerShownByWallet(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_TOP_STAKER_SHOWN_BY_WALLET)
    return raw ? JSON.parse(raw) as Record<string, boolean> : {}
  } catch {
    return {}
  }
}

function hasTopStakerFormBeenShown(address?: string): boolean {
  if (!address) return false
  return getTopStakerShownByWallet()[address] === true
}

function setTopStakerFormShown(address?: string): void {
  if (!address) return
  try {
    localStorage.setItem(
      STORAGE_KEY_TOP_STAKER_SHOWN_BY_WALLET,
      JSON.stringify({
        ...getTopStakerShownByWallet(),
        [address]: true,
      }),
    )
  } catch {
    // ignore
  }
}

export function useScheduledForms() {
  const currentMonthKey = ref<string>(getCurrentMonthKey())
  const lastOpenMonth = ref<string | null>(getLastOpenMonth())

  const isInWindow = computed<boolean>(() => isWithinLastNDays(DAYS_WINDOW))

  const isDue = computed<boolean>(() => {
    if (!isInWindow.value) return false
    return lastOpenMonth.value !== currentMonthKey.value
  })

  function markOpened(): void {
    const key = getCurrentMonthKey()
    lastOpenMonth.value = key
    setLastOpenMonth(key)
  }

  async function openScheduledForm(options?: { address?: string }) {
    if (!isDue.value) return false
    const formId = TallyFormId.SCHEDULED_FORM
    const didOpen = await initTallyForm(options?.address, formId)
    if (didOpen) {
      markOpened()
    }
    return didOpen
  }

  async function openTopStakerFormOnce(options?: { address?: string }) {
    if (hasTopStakerFormBeenShown(options?.address)) return false
    const formId = TallyFormId.TOP_STAKER
    const didOpen = await initTallyForm(options?.address, formId)
    if (didOpen) {
      setTopStakerFormShown(options?.address)
    }
    return didOpen
  }

  return {
    // state
    isInWindow,
    isDue,
    // actions
    openScheduledForm,
    openTopStakerFormOnce,
    markOpened,
  }
}

export type UseScheduledForms = ReturnType<typeof useScheduledForms>
